import { AssignmentInd, Delete, Edit } from '@mui/icons-material';
import { TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Typography, Button, Box, Avatar } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { avatarColorFromName } from 'src/shared/helpers/avatarColor';
import { OrganizationRoleButton } from './organizationRoleButton';
import { EmptyGridCell } from '../atoms/emptyGridCell';

type OrganizationRoleTableProps<T> = {
  roles: Array<T>;
  idColumn: string;
  titleColumn: string;
  valueColumn: string;
  admDateColumn: string;
  resDateColumn: string;
  setUserCallback: (id: any) => void;
  editDateCallback: (id: any) => void;
  deleteCallback: (id: any) => void;
  // Om satt visas en kolumn "Användare" med e-post för poster där medlemmen även har inloggning.
  userEmailColumn?: string;
  // Om satt gråtonas rader där row[historicalColumn] är true (avslutade uppdrag) och åtgärdsknappar döljs.
  historicalColumn?: string;
  // Om satt döljs åtgärdsknapparna när row[canUseColumn] är false. Uppdraget hör till en annan nivå
  // och går att se men inte att tilldela eller ändra.
  canUseColumn?: string;
};

// yyyyMMdd -> yyyy-MM-dd (lämnar andra värden orörda).
const formatDate8 = (value: string) => (/^\d{8}$/.test(value || '') ? `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}` : value);

const getInitials = (fullName: string) => {
  const parts = (fullName || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  const first = parts[0].charAt(0);
  const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';
  return `${first}${last}`.toUpperCase();
};

export const OrganizationRoleTable = <T,>(props: OrganizationRoleTableProps<T>) => {
  const {
    roles,
    idColumn,
    titleColumn,
    valueColumn,
    admDateColumn,
    resDateColumn,
    setUserCallback,
    editDateCallback,
    deleteCallback,
    userEmailColumn,
    historicalColumn,
    canUseColumn,
  } = props;
  const { t } = useTranslation(['common']);
  return (
    <TableContainer>
      <Table aria-label='simple table' size='medium'>
        <TableHead>
          <TableRow sx={{ color: (thm) => thm.palette.text.secondary, fontWeight: 500 }}>
            <TableCell>{t('common:organization.tableHeader.title')}</TableCell>
            <TableCell>{t('common:organization.tableHeader.name')}</TableCell>
            <TableCell>{t('common:organization.tableHeader.date')}</TableCell>
            <TableCell>{t('common:organization.tableHeader.resDate')}</TableCell>
            {userEmailColumn && <TableCell>{t('common:organization.tableHeader.user')}</TableCell>}
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {roles.map((row) => {
            const name = row[valueColumn] as unknown as string;
            const isDisabled = !name;
            const email = userEmailColumn ? (row[userEmailColumn] as unknown as string) : '';
            const isHistorical = historicalColumn ? !!row[historicalColumn] : false;
            const isReadOnly = canUseColumn ? row[canUseColumn] === false : false;
            return (
              <TableRow
                key={row[idColumn]}
                hover
                sx={
                  isHistorical
                    ? {
                        backgroundColor: 'grey.100',
                        '& .MuiTableCell-root, & .MuiTypography-root': { color: 'text.disabled' },
                      }
                    : undefined
                }
              >
                <TableCell component={'th' as any} scope='row'>
                  <Typography variant='body1' sx={{ fontWeight: 600 }}>
                    {row[titleColumn]}
                  </Typography>
                </TableCell>
                <TableCell>
                  {isDisabled && isReadOnly ? (
                    <Typography variant='body2' sx={{ color: 'text.disabled' }}>
                      {t('common:organization.vacantLabel')}
                    </Typography>
                  ) : null}
                  {isDisabled && !isReadOnly ? (
                    <OrganizationRoleButton
                      buttonValue=''
                      vacantLabel={t('common:organization.vacantLabel')}
                      editDateAction={() => editDateCallback(row[idColumn])}
                      setUserAction={() => setUserCallback(row[idColumn])}
                    />
                  ) : null}
                  {!isDisabled ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <Avatar sx={{ width: 32, height: 32, fontSize: 13, flexShrink: 0, bgcolor: avatarColorFromName(name) }}>
                        {getInitials(name)}
                      </Avatar>
                      <Typography variant='body2' sx={{ whiteSpace: 'nowrap' }}>
                        {name}
                      </Typography>
                    </Box>
                  ) : null}
                </TableCell>
                <TableCell>{row[admDateColumn] ? formatDate8(row[admDateColumn] as unknown as string) : <EmptyGridCell />}</TableCell>
                <TableCell>{row[resDateColumn] ? formatDate8(row[resDateColumn] as unknown as string) : <EmptyGridCell />}</TableCell>
                {userEmailColumn && (
                  <TableCell>
                    {email ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <AssignmentInd fontSize='small' sx={{ color: (thm) => thm.palette.primary.main }} />
                        <Typography variant='body2' sx={{ whiteSpace: 'nowrap' }}>
                          {email}
                        </Typography>
                      </Box>
                    ) : (
                      <EmptyGridCell />
                    )}
                  </TableCell>
                )}
                <TableCell align='right'>
                  {isDisabled || isHistorical || isReadOnly ? (
                    <EmptyGridCell />
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                      <Button variant='outlined' size='small' startIcon={<Edit />} onClick={() => editDateCallback(row[idColumn])}>
                        {t('common:button.edit')}
                      </Button>
                      <Button variant='outlined' size='small' color='error' startIcon={<Delete />} onClick={() => deleteCallback(row[idColumn])}>
                        {t('common:button.delete')}
                      </Button>
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
